import React from "react";
import { useParams, Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";

// Blog data with actual content
const blogPosts = {
  1: {
    id: 1,
    title: "The Science Of Sleep",
    slug: "the-science-of-sleep",
    image: "/images/Bella/Blog Photos/The Science Of Sleep.JPG",
    date: "December 10, 2025",
    author: "BellaFlex Team",
    excerpt:
      "Discover the fascinating science behind quality sleep and how it impacts your daily life.",
    content: [
      "A good night's sleep isn't a luxury — it's a biological necessity. From the moment you close your eyes, your body begins repairing tissues, balancing hormones, and recharging your mind for the next day. Yet many people overlook one of the most important elements of healthy sleep: the mattress they lie on every night.",
      "At Bellaflex, we believe better sleep starts with better science. Here's how the right mattress can completely elevate your health and daily performance.",

      "Your Mattress Directly Impacts Your Spine and Muscles",
      "When you sleep on a mattress that doesn't support your natural spinal curve, your body spends the entire night compensating — leading to morning stiffness, back pain, and restless sleep. A high-quality mattress keeps the spine aligned, reduces pressure on joints, and helps your muscles fully relax. This means fewer interruptions at night and more energy during the day.",

      "Sleep Quality Shapes Your Brain, Mood, and Productivity",
      "Deep sleep plays a huge role in memory, focus, and emotional stability. Poor sleep affects everything from stress levels to decision-making. The right mattress helps you fall asleep faster, stay asleep longer, and enter deeper sleep stages — the phases where the brain detoxifies, repairs, and resets.",

      "Materials Matter More Than You Think",
      "Not all mattresses are created equal. Breathable foams, cooling layers, and advanced support systems make a noticeable difference in temperature regulation and comfort. Bellaflex mattresses are designed with high-density supportive layers, pressure-relieving comfort foam, breathable fabric for airflow, and temperature-balancing technology. This combination keeps the body supported, cool, and deeply relaxed through the night.",

      "Better Sleep Strengthens Immunity and Overall Health",
      "Quality sleep boosts the immune system, helps regulate appetite, and supports heart health. When your body rests properly, everything from metabolism to mood improves naturally. A supportive mattress reduces nighttime tossing and turning, giving your body the uninterrupted rest it needs to function at its best.",

      "Sleep Better, Live Better",
      "Your mattress is one of the most important health investments you can make. With Bellaflex, every detail is engineered to improve sleep quality, restore comfort, and elevate your everyday wellbeing. Discover the science of better sleep — and wake up to a healthier, more energized you.",
    ],
  },
  2: {
    id: 2,
    title: "Why Smart Sleep Starts with Knowing Your Body",
    slug: "how-to-find-the-perfect-mattress",
    image: "/images/Bella/Product%20Photos/sensorAI.PNG",
    date: "December 5, 2025",
    author: "BellaFlex Team",
    excerpt: "How pressure & heat sensors are redefining the way we sleep",
    content: [
      "For years, choosing a mattress has relied on guesswork—lying down for a few minutes in a showroom, pressing the surface with a hand, or trusting generic firmness labels. But real sleep happens over eight hours, not eight seconds. Today, smart sensor technology is transforming the way we sleep by focusing on what your body actually needs, not assumptions.",

      "Your Body Tells a Sleep Story Every Night",
      "While you sleep, your body produces measurable signals that reveal how well you are supported. Pressure builds up in certain areas, weight is distributed unevenly, and heat is released throughout the night. These factors directly impact blood circulation, spinal alignment, muscle recovery, and sleep depth. Traditional mattresses treat all sleepers the same, but smart sensors read your body as an individual.",

      "How the Pressure & Heat Sensor Works",
      "Bellaflex’s advanced sensor technology analyzes your body in real time by measuring how pressure is distributed across key zones such as the shoulders, hips, and lower back, while also detecting how your body temperature interacts with mattress materials. Within minutes, the system creates a personalized sleep profile that reveals how your body should be supported throughout the night for optimal rest.",

      "Why Pressure Mapping Matters",
      "Uneven pressure is one of the main causes of tossing and turning, numbness or tingling, and waking up with stiffness or fatigue. By identifying pressure points, the sensor helps match you with a mattress that redistributes weight evenly, reduces stress on joints, and maintains proper spinal alignment. These elements are essential for truly restorative sleep.",

      "Heat Levels Reveal More Than Comfort",
      "Body temperature plays a critical role in sleep quality. When a mattress traps heat, sleep cycles are disrupted and deep rest becomes harder to achieve. The sensor detects how much heat your body generates and retains, allowing Bellaflex to recommend breathable structures, targeted cooling layers, and materials that naturally regulate temperature. The result is a cooler, deeper, and more consistent sleep experience.",

      "From Guesswork to Precision Sleep",
      "What feels comfortable to one person may be harmful to another. Firmness labels, trends, and general recommendations cannot replace real data. With sensor-guided matching, your mattress choice is based on how your body truly interacts with the surface—your pressure distribution, heat profile, and individual support needs.",

      "This is sleep personalization, backed by science.",
    ],
  },
  3: {
    id: 3,
    title: "Inside the Craftsmanship Behind Bellaflex Mattresses",
    slug: "craftsmanship",
    image: "/images/Bella/Blog Photos/Craftsmanship.jpg",
    date: "November 28, 2025",
    author: "BellaFlex Team",
    excerpt:
      "Learn about the meticulous craftsmanship that goes into every BellaFlex mattress.",
    content: [
      "At Bellaflex, we don't just make mattresses — we craft experiences. Every detail, from design to material selection, is guided by one philosophy: comfort meets innovation. Because true luxury isn't just about indulgence — it's about the quality of your sleep, night after night.",

      "Comfort Meets Innovation",
      "Bellaflex believes a mattress should do more than feel good. It should support your body, improve your sleep quality, and enhance your daily performance. By combining cutting-edge sleep technology with timeless comfort, Bellaflex ensures every night is restorative, rejuvenating, and deeply satisfying.",

      "Premium Materials for Unmatched Sleep",
      "Our mattresses are crafted using only the finest materials: high-density foams for lasting support and durability, pressure-relieving comfort layers for ultimate relaxation, and cooling and breathable fabrics to regulate temperature and keep you comfortable all night. Every layer is thoughtfully designed to balance softness, support, and airflow, ensuring an exceptional sleep experience.",

      "Precision Craftsmanship",
      "Bellaflex mattresses are handcrafted with meticulous attention to detail. Every seam, layer, and component is carefully assembled to meet the highest standards. This craftsmanship not only ensures durability but also creates a product that looks as luxurious as it feels.",

      "Rigorous Quality Testing",
      "Before reaching your bedroom, every Bellaflex mattress undergoes extensive testing. From durability and firmness to airflow and pressure distribution, our mattresses are certified to meet the strictest quality standards. This commitment guarantees that your investment delivers consistent comfort and support for years to come.",

      "Luxury Sleep: An Investment in Daily Performance",
      "A premium mattress is more than a purchase — it's an investment in your health, energy, and wellbeing. With Bellaflex, luxury sleep translates into better focus, improved mood, and the physical restoration your body deserves.",

      "Discover the Bellaflex Difference",
      "Experience the perfect blend of craftsmanship, innovation, and comfort. Explore our collection today and wake up to the luxury of truly restorative sleep.",
    ],
  },
};

const BlogDetailPage = () => {
  const { slug } = useParams();

  // Find blog post by slug
  const post = Object.values(blogPosts).find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Blog Post Not Found
          </h1>
          <Link to="/" className="text-brand-blue hover:underline">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-4 md:pt-24 pb-12">
      <SEOHead
        title={post.title}
        description={post.excerpt}
        canonical={`${window.location.origin}/blog/${post.slug}`}
      />

      {/* Hero Image */}
      <div className="relative h-[400px] md:h-[500px] mb-12">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <Link to="/" className="text-brand-blue hover:underline">
            Home
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-600">Blog</span>
        </nav>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          {post.title}
        </h1>

        {/* Meta */}
        <div className="flex items-center gap-4 text-gray-600 mb-12 pb-8 border-b border-gray-200">
          <span>{post.date}</span>
          <span>•</span>
          <span>By {post.author}</span>
        </div>

        {/* Content Paragraphs */}
        <div className="prose prose-lg max-w-none">
          {post.content.map((paragraph, index) => {
            // Check if paragraph is a heading (shorter, no punctuation at end)
            const isHeading =
              paragraph.length < 100 && !paragraph.endsWith(".");

            if (isHeading) {
              return (
                <h2
                  key={index}
                  className="text-2xl font-bold text-gray-900 mt-8 mb-4"
                >
                  {paragraph}
                </h2>
              );
            }

            return (
              <p key={index} className="text-gray-700 leading-relaxed mb-6">
                {paragraph}
              </p>
            );
          })}
        </div>

        {/* Back to Blog */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/"
            className="inline-flex items-center text-brand-blue hover:underline font-medium"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Home
          </Link>
        </div>
      </article>
    </div>
  );
};

export default BlogDetailPage;
